import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plug,
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Shield,
  Users,
  BarChart3,
  Zap,
  Link2,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

export default function IntegrationsPage() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState(null);
  
  // Lotame state
  const [lotameConfig, setLotameConfig] = useState(null);
  const [showLotameDialog, setShowLotameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [lotameForm, setLotameForm] = useState({
    client_id: '',
    api_key: '',
    api_secret: '',
    api_endpoint: 'https://api.lotame.com/2',
    enabled: true,
  });

  useEffect(() => {
    if (isAdmin) {
      loadIntegrations();
    }
  }, [isAdmin]);

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const [statusRes, lotameRes] = await Promise.all([
        api.get('/integrations/status'),
        api.get('/integrations/lotame/config'),
      ]);
      
      setIntegrations(statusRes.data);
      if (lotameRes.data.configured) {
        setLotameConfig(lotameRes.data);
        setLotameForm({
          client_id: lotameRes.data.client_id || '',
          api_key: '', // Don't show existing key
          api_secret: '',
          api_endpoint: lotameRes.data.api_endpoint || 'https://api.lotame.com/2',
          enabled: lotameRes.data.enabled,
        });
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLotame = async () => {
    if (!lotameForm.client_id || !lotameForm.api_key) {
      toast.error('Client ID and API Key are required');
      return;
    }
    
    try {
      await api.post('/integrations/lotame/config', lotameForm);
      toast.success('Lotame configuration saved');
      setShowLotameDialog(false);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await api.post('/integrations/lotame/test');
      if (res.data.connected) {
        toast.success('Successfully connected to Lotame API');
      } else {
        toast.error(res.data.message || 'Connection failed');
      }
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDeleteLotame = async () => {
    try {
      await api.delete('/integrations/lotame/config');
      toast.success('Lotame integration removed');
      setLotameConfig(null);
      setLotameForm({
        client_id: '',
        api_key: '',
        api_secret: '',
        api_endpoint: 'https://api.lotame.com/2',
        enabled: true,
      });
      setShowDeleteDialog(false);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove integration');
    }
  };

  const handleToggleEnabled = async () => {
    if (!lotameConfig) return;
    
    try {
      await api.post('/integrations/lotame/config', {
        ...lotameForm,
        client_id: lotameConfig.client_id,
        api_key: lotameForm.api_key || 'existing', // Keep existing if not changed
        enabled: !lotameConfig.enabled,
      });
      toast.success(lotameConfig.enabled ? 'Lotame disabled' : 'Lotame enabled');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="glass-card border-zinc-800 p-8">
          <div className="text-center">
            <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Admin Access Required</h2>
            <p className="text-zinc-400">Only administrators can manage integrations.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Integrations
          </h1>
          <p className="text-zinc-400 mt-1">
            Manage third-party service connections for audience data and programmatic advertising
          </p>
        </div>
        <Button onClick={loadIntegrations} variant="outline" className="border-zinc-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Lotame Integration */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Lotame DMP</CardTitle>
                  <CardDescription className="text-zinc-500">
                    Audience segmentation & identity resolution
                  </CardDescription>
                </div>
              </div>
              {lotameConfig?.configured ? (
                <Badge className={
                  lotameConfig.is_connected
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                }>
                  {lotameConfig.is_connected ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</>
                  ) : (
                    <><AlertTriangle className="w-3 h-3 mr-1" /> Not Connected</>
                  )}
                </Badge>
              ) : (
                <Badge className="bg-zinc-700 text-zinc-400 border-zinc-600">
                  <XCircle className="w-3 h-3 mr-1" /> Not Configured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-zinc-800/30 rounded-lg">
              <h4 className="text-white font-medium mb-2">Features:</h4>
              <ul className="text-sm text-zinc-400 space-y-1">
                <li className="flex items-center gap-2">
                  <Zap className="w-3 h-3 text-blue-400" />
                  Match device IDs to audience segments
                </li>
                <li className="flex items-center gap-2">
                  <BarChart3 className="w-3 h-3 text-emerald-400" />
                  Demographics & interests enrichment
                </li>
                <li className="flex items-center gap-2">
                  <Link2 className="w-3 h-3 text-purple-400" />
                  Cross-device identity resolution
                </li>
              </ul>
            </div>
            
            {lotameConfig?.configured && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">Client ID</span>
                  <span className="text-white font-mono text-sm">{lotameConfig.client_id}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">API Endpoint</span>
                  <span className="text-white font-mono text-sm truncate max-w-[200px]">{lotameConfig.api_endpoint}</span>
                </div>
                {lotameConfig.last_sync && (
                  <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-zinc-400">Last Sync</span>
                    <span className="text-white text-sm">
                      {format(new Date(lotameConfig.last_sync), 'MMM d, HH:mm')}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">Enabled</span>
                  <Switch
                    checked={lotameConfig.enabled}
                    onCheckedChange={handleToggleEnabled}
                  />
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              {lotameConfig?.configured ? (
                <>
                  <Button
                    variant="outline"
                    className="flex-1 border-zinc-700"
                    onClick={() => setShowLotameDialog(true)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure
                  </Button>
                  <Button
                    variant="outline"
                    className="border-zinc-700"
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                  >
                    {testingConnection ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plug className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-500"
                  onClick={() => setShowLotameDialog(true)}
                >
                  <Plug className="w-4 h-4 mr-2" />
                  Connect Lotame
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* DV360 Integration */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Google DV360</CardTitle>
                  <CardDescription className="text-zinc-500">
                    Programmatic DOOH advertising
                  </CardDescription>
                </div>
              </div>
              <Badge className={
                integrations?.dv360?.is_connected
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-zinc-700 text-zinc-400 border-zinc-600'
              }>
                {integrations?.dv360?.is_connected ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</>
                ) : (
                  <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
                )}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-zinc-800/30 rounded-lg">
              <h4 className="text-white font-medium mb-2">Features:</h4>
              <ul className="text-sm text-zinc-400 space-y-1">
                <li className="flex items-center gap-2">
                  <Zap className="w-3 h-3 text-blue-400" />
                  Browse DOOH inventory
                </li>
                <li className="flex items-center gap-2">
                  <BarChart3 className="w-3 h-3 text-emerald-400" />
                  Campaign management
                </li>
                <li className="flex items-center gap-2">
                  <Link2 className="w-3 h-3 text-purple-400" />
                  Performance reporting
                </li>
              </ul>
            </div>
            
            {integrations?.dv360?.is_connected ? (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">Service Account configured</span>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-sm">
                  Upload a Google Service Account JSON file in the DV360 page to enable this integration.
                </p>
              </div>
            )}
            
            <Button
              variant="outline"
              className="w-full border-zinc-700"
              onClick={() => window.location.href = '/dv360'}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Go to DV360 Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* API Documentation */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">Integration API Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-zinc-950 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre className="text-zinc-300">
{`# Lotame Integration Endpoints (Admin Only)

# Save configuration
POST /api/integrations/lotame/config
{
  "client_id": "your-lotame-client-id",
  "api_key": "your-api-key",
  "api_secret": "optional-secret",
  "api_endpoint": "https://api.lotame.com/2",
  "enabled": true
}

# Get configuration (secrets hidden)
GET /api/integrations/lotame/config

# Test connection
POST /api/integrations/lotame/test

# Remove integration
DELETE /api/integrations/lotame/config

# Get all integrations status
GET /api/integrations/status`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Lotame Configuration Dialog */}
      <Dialog open={showLotameDialog} onOpenChange={setShowLotameDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              {lotameConfig?.configured ? 'Update Lotame Configuration' : 'Connect to Lotame'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Enter your Lotame DMP API credentials to enable audience segmentation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Client ID *</Label>
              <Input
                value={lotameForm.client_id}
                onChange={(e) => setLotameForm({ ...lotameForm, client_id: e.target.value })}
                placeholder="Your Lotame Client ID"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">API Key *</Label>
              <div className="relative">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={lotameForm.api_key}
                  onChange={(e) => setLotameForm({ ...lotameForm, api_key: e.target.value })}
                  placeholder={lotameConfig?.has_api_key ? '••••••••••••' : 'Your API Key'}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {lotameConfig?.has_api_key && (
                <p className="text-zinc-500 text-xs mt-1">Leave blank to keep existing key</p>
              )}
            </div>
            
            <div>
              <Label className="text-zinc-300">API Secret (Optional)</Label>
              <Input
                type="password"
                value={lotameForm.api_secret}
                onChange={(e) => setLotameForm({ ...lotameForm, api_secret: e.target.value })}
                placeholder={lotameConfig?.has_api_secret ? '••••••••••••' : 'Optional API Secret'}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">API Endpoint</Label>
              <Input
                value={lotameForm.api_endpoint}
                onChange={(e) => setLotameForm({ ...lotameForm, api_endpoint: e.target.value })}
                placeholder="https://api.lotame.com/2"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <Label className="text-zinc-300">Enable Integration</Label>
                <p className="text-zinc-500 text-xs mt-1">Turn off to disable without removing</p>
              </div>
              <Switch
                checked={lotameForm.enabled}
                onCheckedChange={(checked) => setLotameForm({ ...lotameForm, enabled: checked })}
              />
            </div>
            
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-blue-400 text-sm">
                <strong>How to get credentials:</strong>
              </p>
              <ol className="text-blue-300 text-xs mt-2 space-y-1 list-decimal list-inside">
                <li>Log in to your Lotame Platform account</li>
                <li>Navigate to My Account → API Keys</li>
                <li>Create a new API key or use an existing one</li>
                <li>Copy your Client ID and API Key</li>
              </ol>
              <a 
                href="https://platform.lotame.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs mt-2"
              >
                <ExternalLink className="w-3 h-3" />
                Open Lotame Platform
              </a>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLotameDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveLotame} className="bg-purple-600 hover:bg-purple-500">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Lotame Integration?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will remove all Lotame configuration including your API credentials. 
              Audience segmentation will fall back to mock data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLotame}
              className="bg-red-600 hover:bg-red-500"
            >
              Remove Integration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
